/**
 * The intervals this user actually reaches for, most recently used first — plus
 * the two functions that take an interval apart and put one back together.
 *
 * ## Why an interval is not a count and a unit
 *
 * The contract's `Frequency` is an enum of exactly eleven strings, and it is
 * **not** the cross-product of the counts and units that appear in it. There is
 * `1H` and `4H` but no `2H`; `1D` but no `7D`; `15m` but no `10m`. So anything
 * that lets a user type a count has to be able to answer "that one does not
 * exist" — which is why `composeInterval` returns `null` rather than the nearest
 * legal neighbour. Snapping `7D` to `1D` would chart a different question from
 * the one that was asked and present the result as the answer; a disabled button
 * is worse UI and a better outcome.
 *
 * ## The case trap, and how it was removed
 *
 * `m` was minute and `M` was month — the same letter, distinguished only by
 * case. Every comparison here had to be case-sensitive, because a single
 * `.toLowerCase()` in this file turned a one-month chart into a one-minute one,
 * silently, with plausible data to match.
 *
 * **Minute is now `mi`**, so no two units differ by case and the trap is gone
 * rather than guarded. The regex carries an `i` flag, `splitInterval` returns
 * the canonical lowercase unit, and `normalizeOrder` *repairs* a stored `"1D"`
 * instead of dropping it. See `docs/decisions.md` for the decision; the twin of
 * this module is `services/qv-data/app/qf/interval.py` and the two must keep
 * agreeing about what parses.
 *
 * ## Why localStorage
 *
 * The recents list is view state, the same kind of thing as `group-selection.ts`
 * — and the contract has nowhere to put it: `ChartGroup.frequency` records the
 * interval in use, never the three you move between. Unlike the group selection
 * it is deliberately *not* keyed by section: "the intervals I work in" is a
 * habit of the person, not a property of one chart, so two chart groups sharing
 * one list is the useful behaviour rather than a collision. If the contract ever
 * grows a per-user preferences object, this belongs in it and should sync.
 *
 * Every storage path is failure-tolerant — Safari private mode throws on
 * `localStorage` access, and an interval picker is not worth a blank app.
 */

import type { Frequency } from "@qufolio/model";

/**
 * The intervals the dropdown offers.
 *
 * **Not a whitelist.** The contract states a pattern, so any count on a known
 * unit is legal and the server resamples to it; this is the conventional set a
 * picker starts from. Nothing may test membership here to decide whether an
 * interval is valid — `isInterval` checks shape for exactly that reason.
 */
export const INTERVALS: readonly Frequency[] = [
  // Seconds are served and still parse, but are **not offered** (owner,
  // 2026-09-28): Massive's bars API withholds same-day 1s, so a seconds chart
  // shows no bars for today. Back when `docs/plans/intraday-service.md` takes
  // 1s up again. A stored `5s` chart keeps working as before.
  "1mi",
  "5mi",
  "15mi",
  "30mi",
  "1h",
  "4h",
  "1d",
  "1w",
  "1m",
  "3m",
  "12m",
];

/**
 * The unit half of an interval, canonical lowercase.
 *
 * `mi` is minute and `m` is month. They are two letters apart rather than one
 * case apart, which is the whole reason everything else here can fold.
 */
export type IntervalUnit = "s" | "mi" | "h" | "d" | "w" | "m";

/** How many recents the picker puts on screen. */
/**
 * Buttons in the row: the current interval's unit, then two recent intervals.
 *
 * The first slot is not a history entry — it shows the *unit* of whatever is
 * current, with the count living in the box beside it, so `2D` reads as `2` and
 * `D` and the count is directly editable. The other two are whole intervals
 * (`1W`, `4H`), because a history entry is a place to jump back to rather than
 * something to edit in place.
 */
export const MRU_SLOTS = 3;

/**
 * What a user who has never chosen an interval starts with.
 *
 * Daily first because that is what a chart opens on, then weekly and hourly —
 * one step either side of it. An empty bar with nothing to click would make the
 * first use of the picker strictly worse than the `<select>` it replaced.
 */
export const SEED: readonly Frequency[] = ["1d", "1w", "1h"];

const KEY = "qufolio.chart.intervals";

// Deliberately the same shape as the schema's `frequency` pattern, and for the
// same reasons: `[1-9][0-9]*` because zero bars is not an interval and `01d`
// is not a spelling; and anchored, because a partial match is how a typo
// becomes a silently different timeframe.
//
// **`mi` is listed before `m`** so the two-letter unit wins the alternation
// outright rather than by backtracking — the ordering is doing real work here,
// not tidiness. The `i` flag is safe now and would not have been before: while
// minute was `m` and month `M`, it was the one change that made them the same
// interval.
const INTERVAL = /^([1-9][0-9]*)(mi|s|h|d|w|m)$/i;

/** Whether an arbitrary value is an interval, in any casing. */
export function isInterval(value: unknown): value is Frequency {
  // Shape, not membership. `INTERVALS` is what a dropdown offers, never what is
  // legal — checking against it here silently dropped `2d` from the recents,
  // because a value the contract allows is not in that list.
  return typeof value === "string" && INTERVAL.test(value);
}

/** `15m` to `{ count: 15, unit: "mi" }`; `1D` to `{ count: 1, unit: "d" }`. */
export function splitInterval(frequency: Frequency): { count: number; unit: IntervalUnit } {
  const match = INTERVAL.exec(frequency);
  // Unreachable for a contract `Frequency`. The fallback keeps the signature
  // total rather than making every caller handle a null that cannot happen.
  if (!match) return { count: 1, unit: "d" };
  return { count: Number(match[1]), unit: match[2]!.toLowerCase() as IntervalUnit };
}

/**
 * An interval in its canonical spelling, or `null` if it is not one.
 *
 * `1D` and `1d` are one interval, so exactly one of them may reach storage or a
 * comparison — otherwise the recents list shows the same timeframe twice and
 * `promote` cannot tell them apart.
 */
export function canonicalInterval(value: unknown): Frequency | null {
  if (!isInterval(value)) return null;
  const { count, unit } = splitInterval(value as Frequency);
  return `${count}${unit}` as Frequency;
}

/**
 * `4` and `H` to `4H`; `7` and `D` to `null`.
 *
 * Membership of `INTERVALS` is the only test, so this cannot invent an interval
 * the contract does not have — and a caller that only ever dispatches a non-null
 * result cannot send one either.
 */
export function composeInterval(count: number, unit: IntervalUnit): Frequency | null {
  // Any whole count above zero. The contract states a pattern rather than an
  // enum, so `2D` and `45m` are as legal as `1D` and the server resamples to
  // them — there is no set to check membership in, and checking one would
  // reject intervals the contract allows.
  return Number.isInteger(count) && count > 0 ? (`${count}${unit}` as Frequency) : null;
}


/** What a count box holds, as a number — or `null` if it is not a bar count. */
export function parseCount(raw: string): number | null {
  const digits = raw.trim();
  if (!/^\d+$/.test(digits)) return null;
  const count = Number(digits);
  return count > 0 ? count : null;
}


/**
 * Move one interval to the front, keeping the rest in order.
 *
 * Promoting the interval that is already first is a no-op in content, which is
 * what makes it safe to call this on every change of the current interval
 * rather than only on a click.
 */
export function promote(order: readonly Frequency[], used: Frequency): Frequency[] {
  return [used, ...order.filter((frequency) => frequency !== used)];
}

/**
 * Whatever came out of storage, as a usable order.
 *
 * Anything shaped wrong is dropped — a hand-edited `"7x"`, a unit that no longer
 * exists. Anything merely spelled in an older casing is **repaired** rather than
 * dropped: a list written before minute became `mi` says `"1D"`, and losing
 * someone's recents over a case change would be a worse answer than fixing it.
 * De-duplication happens after canonicalising, so `"1D"` and `"1d"` cannot both
 * survive as separate buttons for one timeframe.
 *
 * The seed is then appended for any interval the list does not already contain,
 * so there are always at least three to show: a user who has only ever chosen
 * one would otherwise get one button and two gaps.
 *
 * `"1m"` from an old list meant a *minute* and now reads as a month. It is left
 * as the new meaning for `normalize_frequency`'s reason — nothing in the string
 * says which was intended — and the cost here is one wrong entry in a recents
 * list, not a wrong chart.
 */
export function normalizeOrder(stored: unknown): Frequency[] {
  const raw = Array.isArray(stored) ? stored : [];
  const out: Frequency[] = [];
  for (const entry of raw) {
    const canonical = canonicalInterval(entry);
    if (canonical && !out.includes(canonical)) out.push(canonical);
  }
  for (const seed of SEED) if (!out.includes(seed)) out.push(seed);
  return out;
}

/** The stored order, or the seed. Never throws. */
export function recallIntervals(): Frequency[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    return normalizeOrder(raw ? JSON.parse(raw) : null);
  } catch {
    // Unreadable (private mode) or corrupt JSON — a fresh order is a fine
    // answer, and the next write repairs the entry.
    return normalizeOrder(null);
  }
}

/** Record an interval as the most recently used, and answer the new order. */
export function rememberInterval(used: Frequency): Frequency[] {
  const next = promote(recallIntervals(), used);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable or full — the picker works, it just forgets.
  }
  return next;
}
